import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Share, Plus, Download, Smartphone, Pencil, Check, ExternalLink, Info } from 'lucide-react';

/**
 * InstallBanner — unified PWA install prompt for iOS + Android/desktop.
 *
 * Feb 2026 revision — Safari hardening:
 *  - Robust iPad/iPhone detection (iPadOS 13+ reports as Macintosh with touch).
 *  - Every button gets `type="button"` so it can't accidentally submit a
 *    surrounding form (Safari is strict about this).
 *  - Explicit `onPointerDown` fallback on the Edit toggle — some Safari
 *    builds swallow a `click` when a fixed element is layered above the
 *    Apple share sheet.
 *  - Safe-area-inset padding so the top banner sits below the iPhone notch.
 *  - Uses the real Emerald logo (`/icon-192.png`) inside the banner, not a
 *    plain "E" glyph.
 *  - Higher z-index (`z-[70]`) so it can't hide behind modals.
 *  - When the user picks a different app from the Edit picker we hard-load
 *    the target URL; the head's manifest-swap script then wires the right
 *    manifest + apple-mobile-web-app-title before the OS shortcut is saved.
 */
const KEY_PREFIX = 'emerald_install_dismissed_at:';
const HIDE_DAYS = 30;

// ─── Browser detection ──────────────────────────────────────────────
const uaOf = () => (typeof navigator !== 'undefined' ? (navigator.userAgent || navigator.vendor || '') : '');

const isIOS = () => {
  const ua = uaOf();
  if (typeof window === 'undefined') return false;
  // Classic iPhone / iPod / iPad UAs
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return true;
  // iPadOS 13+ masquerades as Macintosh — detect via touch support.
  if (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document) return true;
  return false;
};
const isSafari = () => /^((?!chrome|android|crios|fxios|edg|opr).)*safari/i.test(uaOf());
const isSamsungBrowser = () => /SamsungBrowser/i.test(uaOf());
const isFirefox = () => /Firefox|FxiOS/i.test(uaOf());
const isInStandaloneMode = () => (
  typeof window !== 'undefined'
  && (
    ('standalone' in window.navigator && window.navigator.standalone)
    || window.matchMedia('(display-mode: standalone)').matches
  )
);

// ─── App / surface catalogue ────────────────────────────────────────
// Every entry is a real, installable PWA identity. `installUrl` is the
// URL we navigate to when the user picks that app — it must be inside
// that manifest's `scope` so the browser reads the correct manifest and
// saves the shortcut with the right icon + name.
const APPS = [
  {
    key: 'workspace',
    label: 'Emerald Workspace',
    tagline: 'HR · CRM · Payroll · Employee Self-Service — one tap.',
    installUrl: '/login',
    match: (p) => (
      p === '/login'
      || p.startsWith('/login/')
      || p.startsWith('/admin')
      || p.startsWith('/hr/me')
    ),
  },
  {
    key: 'books',
    label: 'Emerald Books',
    tagline: 'Double-entry accounting SaaS, mobile-first.',
    installUrl: '/books/login',
    match: (p) => p.startsWith('/books'),
  },
  {
    key: 'author',
    label: 'Emerald Author',
    tagline: 'The Emerald newsroom — always one tap away.',
    installUrl: '/author/login',
    match: (p) => p.startsWith('/author'),
  },
  {
    key: 'portal',
    label: 'Emerald Corporates',
    tagline: 'The official site — news, contacts, portals.',
    installUrl: '/',
    match: () => true, // fallback
  },
];

const appForPath = (pathname) => APPS.find((a) => a.match(pathname)) || APPS[APPS.length - 1];

// Login-like surfaces get the prominent green top banner (users are
// primed to install right before signing in).
const isPrimarySurface = (pathname) => (
  pathname === '/login'
  || pathname.startsWith('/login/')
  || pathname === '/books/login'
  || pathname === '/author/login'
);

const InstallBanner = () => {
  const [visible, setVisible] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [pathname, setPathname] = useState(() => (typeof window !== 'undefined' ? window.location.pathname : '/'));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [override, setOverride] = useState(() => {
    try { return localStorage.getItem('emerald_install_override') || null; } catch { return null; }
  });

  const detectedApp = useMemo(() => appForPath(pathname), [pathname]);
  const activeApp = useMemo(() => APPS.find((a) => a.key === override) || detectedApp, [override, detectedApp]);
  const key = `${KEY_PREFIX}${detectedApp.key}`;
  const onLogin = isPrimarySurface(pathname);

  const decideVisibility = useCallback(() => {
    if (typeof window === 'undefined') return false;
    if (isInStandaloneMode()) return false;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const days = (Date.now() - new Date(raw).getTime()) / (1000 * 60 * 60 * 24);
        if (days < HIDE_DAYS) return false;
      }
    } catch (_) { /* ignore */ }
    return true;
  }, [key]);

  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    let last = window.location.pathname;
    const iv = setInterval(() => {
      if (window.location.pathname !== last) {
        last = window.location.pathname;
        setPathname(last);
      }
    }, 700);
    return () => { window.removeEventListener('popstate', onPop); clearInterval(iv); };
  }, []);

  // Capture native install prompt (Chromium: Chrome / Edge / Brave / Samsung 8+).
  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      if (decideVisibility()) setVisible(true);
    };
    const onInstalled = () => setVisible(false);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [decideVisibility]);

  // Non-Chromium browsers (iOS, Samsung, Firefox, desktop Safari): still
  // render the banner with a manual "Add to Home Screen" hint so users
  // aren't left in the dark.
  useEffect(() => {
    const noPromptBrowser = isIOS() || isSafari() || isSamsungBrowser() || isFirefox();
    if (!noPromptBrowser) return;
    if (decideVisibility()) setVisible(true);
  }, [decideVisibility]);

  // Always show something on the login pages (the whole point of the
  // "install-on-login" pattern), even if no prompt was captured yet.
  useEffect(() => {
    if (!onLogin) return;
    if (decideVisibility()) setVisible(true);
  }, [onLogin, decideVisibility]);

  const dismiss = () => {
    try { localStorage.setItem(key, new Date().toISOString()); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) return;
    try {
      installEvent.prompt();
      const choice = await installEvent.userChoice;
      if (choice?.outcome === 'accepted') {
        setVisible(false);
        try { localStorage.removeItem('emerald_install_override'); } catch (_) { /* ignore */ }
      }
    } catch (_) { /* browser may reject if already handled */ }
    setInstallEvent(null);
  };

  const switchApp = (appKey) => {
    const target = APPS.find((a) => a.key === appKey);
    if (!target) return;
    try { localStorage.setItem('emerald_install_override', appKey); } catch (_) { /* ignore */ }
    setOverride(appKey);
    setPickerOpen(false);
    if (!target.match(window.location.pathname)) {
      window.location.assign(target.installUrl);
    }
  };

  if (!visible) return null;

  const ios = isIOS();
  const samsung = isSamsungBrowser();
  const ff = isFirefox();
  const safariDesktop = !ios && isSafari();

  // Which action block to render:
  //   1) Native prompt available   → "Install app" button
  //   2) iOS Safari                → Share → Add to Home Screen hint
  //   3) Samsung Internet          → Menu (☰) → Add page to → Home screen
  //   4) Firefox                   → ⋮ menu → Install / Add to Home
  //   5) Desktop Safari            → File → Add to Dock (Sonoma+) hint
  //   6) Generic fallback          → Browser menu → Install app
  let action;
  if (installEvent) {
    action = (
      <button
        type="button"
        onClick={install}
        data-testid="install-banner-install-btn"
        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#0f5c3d] hover:bg-[#f7fbf8] text-xs font-semibold shadow-sm"
      >
        <Download className="w-3.5 h-3.5" /> Install {activeApp.label}
      </button>
    );
  } else if (ios) {
    action = (
      <div className="mt-2 text-xs text-white/85 flex items-center gap-1.5 flex-wrap" data-testid="install-banner-ios-hint">
        <span>Tap</span>
        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-white/15">
          <Share className="w-3 h-3 mr-1" /> Share
        </span>
        <span>→</span>
        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-white/15">
          <Plus className="w-3 h-3 mr-1" /> Add to Home Screen
        </span>
        <span className="text-white/70">— saves as “{activeApp.label}”.</span>
      </div>
    );
  } else if (samsung) {
    action = (
      <div className="mt-2 text-xs text-white/85 leading-snug" data-testid="install-banner-samsung-hint">
        Tap the browser <b>Menu (☰)</b> at the bottom → <b>Add page to</b> →
        <b> Home screen</b>. The shortcut will save as “{activeApp.label}”.
      </div>
    );
  } else if (ff) {
    action = (
      <div className="mt-2 text-xs text-white/85 leading-snug">
        Open the <b>⋮ menu</b> → <b>Install</b> (desktop) or
        <b> Add to Home Screen</b> (Android).
      </div>
    );
  } else if (safariDesktop) {
    action = (
      <div className="mt-2 text-xs text-white/85 leading-snug">
        Safari 17+ (macOS Sonoma): choose <b>File</b> → <b>Add to Dock…</b>.
      </div>
    );
  } else {
    action = (
      <div className="mt-2 text-xs text-white/85 leading-snug">
        Open your browser menu and choose <b>“Install app”</b> or
        <b> “Add to Home Screen”</b> to save {activeApp.label}.
      </div>
    );
  }

  return (
    <div
      data-testid="install-banner"
      className={`fixed inset-x-0 z-[70] px-3 pointer-events-none ${onLogin ? 'top-0' : 'bottom-0'}`}
      style={onLogin
        ? { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }
        : { paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
    >
      <div
        className={`pointer-events-auto max-w-md mx-auto text-white rounded-2xl shadow-2xl overflow-hidden border ${
          onLogin ? 'bg-[#0f5c3d] border-[#7bbf95]/40 ring-1 ring-white/10' : 'bg-[#0b1a2e] border-[#0f5c3d]/30'
        }`}
      >
        <div className="px-4 py-3 flex items-start gap-3">
          {/* Actual Emerald logo — reads the site brand mark, not a plain "E". */}
          <img
            src="/icon-192.png"
            alt=""
            aria-hidden="true"
            width={40}
            height={40}
            className="w-10 h-10 rounded-lg flex-shrink-0 shadow-md object-cover"
          />

          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold flex items-center flex-wrap gap-x-1.5 gap-y-1">
              <Smartphone className="w-3.5 h-3.5 text-[#7bbf95]" />
              <span data-testid="install-banner-title">Install {activeApp.label}</span>
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                onPointerDown={(e) => { /* Safari fallback: reset toggle even if click is swallowed */
                  if (e.pointerType === 'touch') { e.stopPropagation(); }
                }}
                data-testid="install-banner-edit-btn"
                aria-label="Change which app to install"
                aria-expanded={pickerOpen}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white text-[#0f5c3d] hover:bg-[#f7fbf8] text-[10px] font-bold shadow-sm"
              >
                <Pencil className="w-3 h-3" /> {pickerOpen ? 'Close' : 'Change'}
              </button>
            </div>
            <div className="text-xs text-white/75 mt-0.5 leading-snug">
              {activeApp.tagline}
            </div>

            {pickerOpen && (
              <div
                className="mt-2 rounded-lg bg-white/10 border border-white/20 overflow-hidden"
                data-testid="install-banner-picker"
              >
                <div className="px-2.5 py-1.5 text-[10px] uppercase tracking-wider text-white/70 border-b border-white/10 flex items-center gap-1">
                  <Info className="w-3 h-3" /> Pick which app to install
                </div>
                {APPS.map((a) => {
                  const active = a.key === activeApp.key;
                  return (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => switchApp(a.key)}
                      data-testid={`install-banner-pick-${a.key}`}
                      className={`w-full flex items-start gap-2 px-2.5 py-2 text-left text-xs hover:bg-white/10 ${active ? 'bg-white/10' : ''}`}
                    >
                      <div className="w-4 h-4 mt-0.5 flex items-center justify-center flex-shrink-0">
                        {active ? <Check className="w-3.5 h-3.5 text-[#7bbf95]" /> : <ExternalLink className="w-3 h-3 text-white/60" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-white leading-tight">{a.label}</div>
                        <div className="text-[10px] text-white/60 truncate">
                          Opens <code className="text-white/70">{a.installUrl}</code> · saves as “{a.label}”
                        </div>
                      </div>
                    </button>
                  );
                })}
                <div className="px-2.5 py-1.5 text-[10px] text-white/60 border-t border-white/10 leading-snug">
                  Picking a different app reloads that page so your phone
                  saves the correct shortcut.
                </div>
              </div>
            )}

            {action}
          </div>

          <button
            type="button"
            onClick={dismiss}
            data-testid="install-banner-dismiss"
            className="p-1 text-white/60 hover:text-white flex-shrink-0"
            aria-label="dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallBanner;
